import { proStudioUrqlGql as urqlGql, Type } from '@pypestream/api-services';
import { TwilioConfigInput } from '@pypestream/api-services/urql';
import { throwErrorWithId } from '@pypestream/utils';

import { Channel } from './get-channels-from-pypes';
import { checkForErrors, DataErrorInternalObject } from './check-for-errors';
import { PypeConfig } from '../xstate/prostudio.api-service.types';
import { haveErrors } from '.';
import { sendUserMessage } from '../xstate/app.xstate-utils';

export type ChannelMutationProps = Omit<
  Channel,
  | 'id'
  | 'parent'
  | 'lastEdited'
  | 'name'
  | 'customPypeCss'
  | 'logo'
  | 'config'
  | 'agentRouting'
  | 'botRetry'
> & {
  id?: string;
  name?: string;
  customPypeCss?: string;
  config?: PypeConfig;
  voiceAiConfig?: TwilioConfigInput;
};

export const mutateChannel = async ({
  channel,
  projectId,
  requestAction,
  cb,
  uniqueCustomerId,
  botId,
}: {
  channel: ChannelMutationProps;
  projectId: string;
  requestAction: string;
  cb?: () => void;
  uniqueCustomerId?: string;
  botId?: string;
}): Promise<{ id?: string } | void> => {
  // Handle voice channels differently
  if (channel.type === 'twilio_voice') {
    return mutateVoiceChannel({
      channel,
      projectId,
      cb,
      uniqueCustomerId,
      botId,
    });
  }

  // Handle widget channels (existing logic)
  const {
    env,
    pypeId,
    id: widgetId = '',
    streamId,
    style,
    beta,
    name = '',
    customPypeCss = '',
    config,
  } = channel;

  if (!config) {
    throwErrorWithId('Config is required for widget channels');
    return;
  }

  const action = requestAction === 'update' ? 'put' : 'post';

  const mutation = {
    post: {
      live: urqlGql.mutatePostWidgetLive,
      sandbox: urqlGql.mutatePostWidgetSandbox,
    },
    put: {
      live: urqlGql.mutatePutWidgetLive,
      sandbox: urqlGql.mutatePutWidgetSandbox,
    },
  };

  const mutateWidget = mutation[action][env];

  const configMutation = {
    live: urqlGql.patchPypeConfigLive,
    sandbox: urqlGql.patchPypeConfigSandbox,
  };

  const mutateConfig = configMutation[env];

  const [res, resConfig] = await Promise.all([
    mutateWidget({
      pypeId,
      projectId,
      ...(requestAction !== 'new' && { widgetId }),
      streamId,
      requestAction,
      style: JSON.stringify(style),
      beta: JSON.stringify(beta),
      widget_name: name,
      custom_pype_css: customPypeCss,
    }),
    mutateConfig({
      pypeId,
      projectId,
      agentRouting: config.agent_routing,
      integrationHeaders: {
        accountId: config.config.integration_headers?.account_id,
        inboxId: config.config.integration_headers?.inbox_id,
      },
    }),
  ]);

  checkForErrors(res);

  const { data, error } = resConfig;
  let errorMessage = '';

  if (haveErrors(data)) {
    errorMessage = data.errors?.[0]?.message;
  }

  if (error) {
    errorMessage =
      (
        error.graphQLErrors?.[0]?.extensions
          ?.internal as DataErrorInternalObject
      )?.response?.body?.errors || error.message;
  }

  if (errorMessage) {
    sendUserMessage({
      type: 'error',
      text: `Error while adding integration to channel: ${errorMessage}`,
      autoClose: 3000,
      position: 'top-right',
    });
  }

  cb?.();

  // Return channel ID if available from the mutation response
  const channelId = widgetId;
  return channelId ? { id: channelId } : undefined;
};

const mutateVoiceChannel = async ({
  channel,
  projectId,
  cb,
  uniqueCustomerId,
  botId,
}: {
  channel: ChannelMutationProps;
  projectId: string;
  cb?: () => void;
  uniqueCustomerId?: string;
  botId?: string;
}) => {
  const { env, streamId, voiceAiConfig, name } = channel;

  if (!voiceAiConfig) {
    throwErrorWithId('Voice AI configuration is required for voice channels');
    return;
  }

  const mutation = {
    live: urqlGql.patchV3ConfigurationCustomersCustomerIdChannelChannelId,
    sandbox:
      urqlGql.patchV3ConfigurationCustomersCustomerIdChannelChannelIdSandbox,
  };

  const mutateVoiceChannelMutation = mutation[env];

  if (!uniqueCustomerId || !botId) {
    throwErrorWithId(
      'uniqueCustomerId and botId are required for voice channel updates'
    );
    return;
  }

  const resp = await mutateVoiceChannelMutation({
    channelId: streamId,
    projectId,
    customerId: uniqueCustomerId,
    v3ConfigurationCustomerChannelInput: {
      config: {
        twilioConfig: voiceAiConfig,
      },
      type: Type.TwilioVoice,
      startBot: botId,
      name,
    },
  });

  checkForErrors(resp);

  cb?.();
};
